/**
 * Pure calculations behind the Leadership view (Phase 5A), used by GET /leadership and by the ops job's
 * quarterly snapshots, so a trend point and today's number are always the same measure.
 *
 * Network figures follow the network benchmark rules (migration 0008): a median is published only when at
 * least five firms have results, and self-assessed firms are never mixed into network figures (0011).
 */
import { isFormerFirm, joinCurrentFirms, whereActiveCurrent } from './former-firms.js';
import { CONTROL_SUMMARIES } from './plain-language.js';
import { BTI_DOMAINS, BTI_WAVE1_EVALUATION, BTI_WAVE2_ITEMS } from './bti-evaluation.js';
import { btiFirmStatus, LEADERSHIP_CYCLE, standingOf, surveyAsBtiStatus } from './leadership.js';
import { PROVENANCE } from './vocabulary.js';
import { countStatuses, criticalCoverage, effectiveRows, firmSource, isInPlace, isPartly, isRated, pct1, scoreFirm, surveyBaselines } from './scoring.js';
/** The fewest firms a network figure may come from, so no single firm's result can be read back. */
export const MIN_NETWORK_FIRMS = 5;
/** The median as SQL Server's PERCENTILE_CONT(0.5) computes it; null for no values. */
export function median(values) {
    if (!values.length)
        return null;
    const s = [...values].sort((a, b) => a - b);
    const mid = (s.length - 1) / 2;
    return (s[Math.floor(mid)] + s[Math.ceil(mid)]) / 2;
}
/** The median to one decimal place, or null when fewer than five firms contribute. */
export function networkMedian(values) {
    const v = values.filter((x) => typeof x === 'number' && Number.isFinite(x));
    const m = v.length >= MIN_NETWORK_FIRMS ? median(v) : null;
    return { value: m === null ? null : Math.round(m * 10) / 10, firms: v.length };
}
/** Action plans by delivery status (firm.action_plan.delivery_status). */
export function planCounts(statuses) {
    const n = (s) => statuses.filter((x) => x === s).length;
    return { onTrack: n('on_track'), atRisk: n('at_risk'), offTrack: n('off_track'), notCommitted: n('not_yet_committed'), complete: n('complete'), total: statuses.length };
}
/** Plans on track or complete, of committed plans (an owner and a target date: everything but not yet committed). */
export function plansOnTrack(c) {
    const onTrack = c.onTrack + c.complete;
    const committed = c.onTrack + c.atRisk + c.offTrack + c.complete;
    return { onTrack, committed, pct: pct1(onTrack, committed) };
}
function waveScore(rows, wave) {
    const c = countStatuses(rows.filter((r) => r.btiWave === wave).map((r) => r.gapStatus));
    return { pct: c.pct, inPlace: c.inPlace, partly: c.partly, total: c.assessed };
}
export function firmMeasures(rows) {
    const s = scoreFirm(rows);
    const critical = criticalCoverage(rows);
    return {
        btcPct: s.btc.pct,
        btcInPlace: s.btc.inPlace,
        btcPartly: s.btc.partly,
        btcTotal: s.btc.assessed,
        btiPct: s.bti.pct,
        btiWave1: waveScore(rows, 1),
        btiWave2: waveScore(rows, 2),
        criticalInPlace: critical.inPlace,
        criticalPartly: critical.partly,
        criticalTotal: critical.total,
        maturity: s.maturity === null ? null : Math.round(s.maturity * 10) / 10,
    };
}
/**
 * Network figures from every active firm. Firms "with results" are those with CDW results (reviewed wins
 * inside them); a firm still self-assessing is left out, as in the network benchmark. Distribution, top
 * gaps and controls to strengthen are empty when fewer than five firms have results.
 */
export function networkFigures(firms, topN = 5) {
    const withResults = firms.filter((f) => f.source === 'cdw' && f.measures.btcPct !== null);
    const enough = withResults.length >= MIN_NETWORK_FIRMS;
    const gapCount = new Map();
    for (const f of withResults) {
        f.rows.forEach((r, order) => {
            if (!isRated(r.gapStatus))
                return;
            const g = gapCount.get(r.controlId) ?? { notYet: 0, partly: 0, rated: 0, order };
            g.rated += 1;
            if (!isInPlace(r.gapStatus))
                g.notYet += 1;
            if (isPartly(r.gapStatus))
                g.partly += 1;
            gapCount.set(r.controlId, g);
        });
    }
    // Most firms first, then the larger share of the firms that rated it, then guidance order.
    const most = (key) => enough
        ? [...gapCount.entries()]
            .filter(([, g]) => g[key] > 0)
            .sort(([, a], [, b]) => b[key] - a[key] || b[key] / b.rated - a[key] / a.rated || a.order - b.order)
            .slice(0, topN)
        : [];
    const topGaps = most('notYet').map(([controlId, g]) => ({ controlId, label: CONTROL_SUMMARIES[controlId] ?? controlId, firmsNotYet: g.notYet, firmsRated: g.rated }));
    const toStrengthen = most('partly').map(([controlId, g]) => ({ controlId, label: CONTROL_SUMMARIES[controlId] ?? controlId, firmsPartly: g.partly, firmsRated: g.rated }));
    const programStages = {};
    for (const f of firms)
        if (f.stage)
            programStages[f.stage] = (programStages[f.stage] ?? 0) + 1;
    const riskRated = withResults.filter((f) => typeof f.higherRisksOpen === 'number');
    return {
        firmsWithResults: withResults.length,
        btc: networkMedian(withResults.map((f) => f.measures.btcPct)),
        btiWave1: networkMedian(withResults.map((f) => f.measures.btiWave1.pct)),
        btiWave2: networkMedian(withResults.map((f) => f.measures.btiWave2.pct)),
        higherRisks: enough && riskRated.length
            ? { open: riskRated.reduce((a, f) => a + f.higherRisksOpen, 0), firmsWith: riskRated.filter((f) => f.higherRisksOpen > 0).length, firmsRated: riskRated.length }
            : null,
        criticalPct: networkMedian(withResults.map((f) => pct1(f.measures.criticalInPlace, f.measures.criticalTotal))),
        fully: {
            btc: networkMedian(withResults.map((f) => pct1(f.measures.btcInPlace - f.measures.btcPartly, f.measures.btcTotal))),
            btiWave1: networkMedian(withResults.map((f) => pct1(f.measures.btiWave1.inPlace - f.measures.btiWave1.partly, f.measures.btiWave1.total))),
            btiWave2: networkMedian(withResults.map((f) => pct1(f.measures.btiWave2.inPlace - f.measures.btiWave2.partly, f.measures.btiWave2.total))),
            criticalPct: networkMedian(withResults.map((f) => pct1(f.measures.criticalInPlace - f.measures.criticalPartly, f.measures.criticalTotal))),
        },
        // Plans are firm-reported, not assessed, so every active firm with committed plans counts.
        plansOnTrackPct: networkMedian(firms.map((f) => plansOnTrack(f.plans).pct)),
        distribution: enough
            ? withResults
                .map((f) => ({ firmCode: f.firmCode, firmName: f.firmName, pct: f.measures.btcPct, partlyPct: pct1(f.measures.btcPartly, f.measures.btcTotal) ?? 0 }))
                .sort((a, b) => b.pct - a.pct || a.firmName.localeCompare(b.firmName))
            : [],
        topGaps,
        toStrengthen,
        programStages,
    };
}
/**
 * How many firms with CDW results have each control in place (fully or partly, with the partly count shown), or
 * not yet (self-assessed firms stay out, as in every network figure). Used for "reported then, measured now"
 * against BTI's evaluation.
 */
export function controlCoverage(firms, controlIds) {
    const withResults = firms.filter((f) => f.source === 'cdw' && f.measures.btcPct !== null);
    return controlIds.map((controlId) => {
        const statuses = withResults.map((f) => f.rows.find((r) => r.controlId === controlId)?.gapStatus).filter((s) => isRated(s ?? null));
        const c = countStatuses(statuses);
        return { controlId, rated: c.assessed, inPlace: c.inPlace, partly: c.partly };
    });
}
/**
 * BTI Wave 2 readiness by BTI area (Andres, 2026-09-26): across the firms with CDW results, how many firm-controls
 * are fully, partly and not yet in place in each area, how many firms have every rated Wave 2 control in the area
 * in place (at least partly) and fully, and the same counts firm by firm. Self-assessed firms stay out, as in every
 * network figure.
 */
export function wave2Readiness(firms) {
    const withResults = firms.filter((f) => f.source === 'cdw' && f.measures.btcPct !== null).sort((a, b) => a.firmName.localeCompare(b.firmName));
    const domains = BTI_DOMAINS.filter((d) => BTI_WAVE2_ITEMS.some((i) => i.domain === d));
    const byFirm = withResults.map((f) => ({
        firmCode: f.firmCode,
        firmName: f.firmName,
        areas: domains.map((domain) => {
            const ids = BTI_WAVE2_ITEMS.filter((i) => i.domain === domain).map((i) => i.controlId);
            const c = countStatuses(ids.map((id) => f.rows.find((r) => r.controlId === id)?.gapStatus));
            return { domain, fully: c.fully, partly: c.partly, notYet: c.notYet, rated: c.assessed };
        }),
    }));
    const areas = domains.map((domain, i) => {
        const rows = byFirm.map((f) => f.areas[i]).filter((a) => a.rated > 0);
        const sum = (k) => rows.reduce((a, r) => a + r[k], 0);
        return {
            domain,
            controls: BTI_WAVE2_ITEMS.filter((x) => x.domain === domain).length,
            fully: sum('fully'),
            partly: sum('partly'),
            notYet: sum('notYet'),
            firmsRated: rows.length,
            firmsAllInPlace: rows.filter((r) => r.notYet === 0).length,
            firmsAllFully: rows.filter((r) => r.fully === r.rated).length,
        };
    });
    return { areas, byFirm };
}
/** Every BTI requirement of both waves, in BTI's order: its wave, its number and the baseline control it maps to. */
export const BTI_REQUIREMENTS = [
    ...BTI_WAVE1_EVALUATION.items.map((i) => ({ wave: 1, itemNo: i.no, controlId: i.controlId })),
    ...BTI_WAVE2_ITEMS.map((i) => ({ wave: 2, itemNo: i.no, controlId: i.controlId })),
];
function surveyFor(survey, controlId) {
    if (!survey)
        return null;
    let found = null;
    for (const a of survey)
        if (a.controlId === controlId)
            found = surveyAsBtiStatus(a.status) ?? found;
    return found;
}
/**
 * 5D (F6): one firm's position today on every BTI requirement, from its own current rows (CDW result, National IT's
 * accepted review, or its own rating), the same rows every other figure of the firm reads. A control with no 2026
 * rating shows the firm's March 2025 survey answer when it has one (DEC-72); otherwise it is not assessed, never
 * counted as not in place.
 */
export function btiFirmItems(rows, survey) {
    return BTI_REQUIREMENTS.map((r) => {
        const row = rows.find((x) => x.controlId === r.controlId);
        const status = btiFirmStatus(row?.gapStatus);
        if (status !== 'not_assessed')
            return { ...r, status, source: row?.source ?? null };
        const fromSurvey = surveyFor(survey, r.controlId);
        if (fromSurvey)
            return { ...r, status: fromSurvey, source: 'survey' };
        return { ...r, status, source: null };
    });
}
/**
 * 5D (F6), National and Board only: every BTI requirement with every active member firm's position today, firms by
 * name (btiFirmItems for each firm). Self-assessed firms and firms with no 2026 rating are listed too, each position
 * saying where it comes from. A firm with no 2026 rating uses its March 2025 survey answer when surveyByFirm has one.
 * None of this enters a network median.
 */
export function btiRequirementFirms(firms, surveyByFirm) {
    const sorted = [...firms].sort((a, b) => a.firmName.localeCompare(b.firmName));
    const byFirm = sorted.map((f) => ({ f, items: btiFirmItems(f.rows, surveyByFirm?.get(f.firmCode)) }));
    return BTI_REQUIREMENTS.map((r, i) => ({
        ...r,
        firms: byFirm.map(({ f, items }) => ({ firmCode: f.firmCode, firmName: f.firmName, status: items[i].status, source: items[i].source })),
    }));
}
/**
 * Every active firm's BTC Baseline with where it comes from, for the named "Where firms stand" (National and
 * Board): CDW results; its own 2026 rating while it waits for CDW ('self'); or, with no 2026 rating at all, its
 * March 2025 survey answers ('survey', achieved of the surveyed controls, no partly split). Highest first, firms
 * with nothing to show last. Network figures never use this list. Derived from the firm-by-firm comparison rows by
 * standingOf, the rule the National view's BTC chart uses too (N2): there is no second copy of it.
 */
export function firmStandings(rows) {
    return rows.map(standingOf).sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1) || a.firmName.localeCompare(b.firmName));
}
/** What the firm-by-firm comparison counts, said once under its table. */
export const FIRM_COMPARISON_MEASURE = `Each firm's figures: controls in place (fully or partly) from its CDW assessment, or from National IT's accepted review when that is newer; maturity and higher risks from the same assessment; action plans and last update as ${PROVENANCE.firm_reported.toLowerCase()}. A firm waiting for CDW shows its own 2026 rating, and a firm with no 2026 rating yet shows its March 2025 survey answers; both are marked, and neither counts in a network median.`;
/**
 * The inputs to firmComparison, under the session's scope. Plain query function, as loadNetworkFirms, so the
 * National view and Leadership read the same rows the same way.
 */
export async function loadFirmComparisonInputs(query) {
    const ACTIVE = joinCurrentFirms('x');
    // Area names only; every figure by area comes from each firm's own rows.
    const domains = await query(`SELECT domain_number AS domainNumber, MIN(domain_name) AS domain FROM core.control GROUP BY domain_number ORDER BY MIN(sort_order)`);
    const survey = await query(`
    SELECT x.firm_code AS firmCode, x.control_id AS controlId, x.normalized_status AS status
    FROM firm.survey_response x ${ACTIVE} WHERE x.cycle_id = N'SURVEY-2025-03' AND x.survey_round = 2 AND x.control_id IS NOT NULL`);
    const tracking = await query(`SELECT x.firm_code AS firmCode, MAX(x.updated_at) AS lastUpdated FROM firm.tracking x ${ACTIVE} GROUP BY x.firm_code`);
    const surveyAnswers = new Map();
    for (const x of survey)
        surveyAnswers.set(x.firmCode, [...(surveyAnswers.get(x.firmCode) ?? []), { controlId: x.controlId, status: x.status }]);
    return {
        domains: domains.map((x) => ({ domainNumber: x.domainNumber, domain: x.domain })),
        surveyAnswers,
        lastUpdated: new Map(tracking.map((x) => [x.firmCode, x.lastUpdated ?? null])),
    };
}
/** A row's fully-in-place share: its in-place share less the points only partly in place, to one decimal. */
const fullyPct = (pct, partlyPct) => (pct === null ? null : Math.round((pct - (partlyPct ?? 0)) * 10) / 10);
const PLAN_KEYS = [['onTrack', 'on_track'], ['atRisk', 'at_risk'], ['offTrack', 'off_track'], ['notCommitted', 'not_yet_committed'], ['complete', 'complete']];
/**
 * Every active member firm side by side, named (National and Board): baselines with the partly share, maturity,
 * critical controls, areas, higher risks, plans, the March 2025 figure and the last update. The one calculation
 * behind the National view and Leadership's "The network" (DEC-22), so a firm's figures agree on both pages.
 * Medians are CDW firms only; self-assessed and survey-only firms are in the rows, marked, and never counted.
 */
export function firmComparison(firms, inputs) {
    const controls = firms[0]?.rows ?? [];
    const iso = (d) => (d instanceof Date ? d.toISOString() : d ? String(d) : null);
    const rows = firms.map((f) => {
        const rated = f.rows.filter((x) => x.gapStatus !== null);
        const s = rated.length ? scoreFirm(f.rows) : null;
        const c = rated.length ? criticalCoverage(f.rows) : null;
        const survey = surveyBaselines(controls, inputs.surveyAnswers.get(f.firmCode) ?? []);
        // No current results (no CDW, review or 2026 self-rating): the baselines as of March 2025, from the firm's own
        // survey answers. Shown beside the others, marked; never counted as a firm with results or in any network figure.
        const march2025 = f.source === null ? survey : null;
        return {
            firmCode: f.firmCode,
            firmName: f.firmName,
            hasResults: f.source === 'cdw',
            source: f.source,
            selfRated: f.rows.filter((x) => x.source === 'self').length,
            controls: f.rows.length,
            btcPct: s?.btc.pct ?? null,
            btcPartly: s?.btc.partly ?? null,
            btiPct: s?.bti.pct ?? null,
            btiPartly: s?.bti.partly ?? null,
            btcPartlyPct: s ? pct1(s.btc.partly, s.btc.assessed) : null,
            btiPartlyPct: s ? pct1(s.bti.partly, s.bti.assessed) : null,
            maturity: s?.maturity ?? null,
            areas: Object.fromEntries((s?.areas ?? []).map((a) => [a.domainNumber, a.pct])),
            areasPartly: Object.fromEntries((s?.areas ?? []).map((a) => [a.domainNumber, pct1(a.partly, a.assessed)])),
            criticalOpen: c ? c.total - c.inPlace : null,
            criticalPartly: c ? c.partly : null,
            criticalRated: c ? c.total : null,
            highRisks: f.higherRisksOpen ?? null,
            actionPlans: Object.fromEntries(PLAN_KEYS.filter(([k]) => f.plans[k] > 0).map(([k, status]) => [status, f.plans[k]])),
            march2025: march2025
                ? {
                    measure: march2025.measure,
                    btc: { pct: march2025.btc.pct, inPlace: march2025.btc.inPlace, surveyed: march2025.btc.surveyed, notCovered: march2025.btc.notCovered },
                    bti: { pct: march2025.bti.pct, inPlace: march2025.bti.inPlace, surveyed: march2025.bti.surveyed, notCovered: march2025.bti.notCovered },
                }
                : null,
            survey2025Pct: survey?.btc.pct ?? null,
            lastUpdated: iso(inputs.lastUpdated.get(f.firmCode)),
        };
    });
    const cdw = rows.filter((r) => r.source === 'cdw' && r.btcPct !== null);
    return {
        measure: FIRM_COMPARISON_MEASURE,
        domains: inputs.domains,
        medians: {
            btc: networkMedian(cdw.map((r) => r.btcPct)).value,
            bti: networkMedian(cdw.map((r) => r.btiPct)).value,
            criticalPct: networkMedian(cdw.map((r) => (r.criticalRated ? pct1(r.criticalRated - (r.criticalOpen ?? 0), r.criticalRated) : null))).value,
            // The same medians counting fully in place only, so a headline built on them shows its partly share (5D B).
            fully: {
                btc: networkMedian(cdw.map((r) => fullyPct(r.btcPct, r.btcPartlyPct))).value,
                bti: networkMedian(cdw.map((r) => fullyPct(r.btiPct, r.btiPartlyPct))).value,
                criticalPct: networkMedian(cdw.map((r) => (r.criticalRated && r.criticalPartly !== null ? pct1(r.criticalRated - (r.criticalOpen ?? 0) - r.criticalPartly, r.criticalRated) : null))).value,
            },
        },
        rows,
    };
}
/**
 * The cycle, its year and the program's name live in leadership.ts (5E pre-gate), so the vocabulary the web and the
 * cards read can name the program too; they are re-exported here, where the API and the PDF have always read them.
 */
export { ASSESSMENT_CYCLE_YEAR, LEADERSHIP_CYCLE, PROGRAM_NAME } from './leadership.js';
/**
 * Higher risks open (alias r = firm.risk): very high and high risks in the CDW risk register with no
 * accepted response and no active risk acceptance.
 */
export const HIGHER_RISKS_OPEN_WHERE = `
  r.cycle_id = N'${LEADERSHIP_CYCLE}' AND r.severity IN (N'very_high', N'high')
  AND NOT EXISTS (SELECT 1 FROM firm.risk_response rr WHERE rr.risk_id = r.risk_id AND rr.state = N'accepted')
  AND NOT EXISTS (SELECT 1 FROM firm.risk_acceptance ra WHERE ra.risk_id = r.risk_id AND ra.status = N'active')`;
/**
 * Every active firm's current status, plans, higher risks and program stage, as the session's Row-Level
 * Security scope allows: all firms for the ops job (system scope) and National or Board readers
 * (read_all), and only their own firm's rows for a firm member (other firms come back with no results).
 * Takes a plain query function so the API and the ops job share one set of queries without this package
 * depending on a database driver. The queries have no parameters.
 */
export async function loadNetworkFirms(query) {
    const ACTIVE = joinCurrentFirms('x');
    const controls = (await query(`
    SELECT control_id AS controlId, domain_number AS domainNumber, domain_name AS domain, framework, bti_wave AS btiWave
    FROM core.control ORDER BY sort_order`));
    const firms = await query(`SELECT firm_code AS firmCode, display_name AS firmName, is_synthetic AS isSynthetic FROM core.firm WHERE ${whereActiveCurrent('firm_code')} ORDER BY firm_code`);
    const cdw = await query(`SELECT x.firm_code AS firmCode, x.control_id AS controlId, x.gap_status AS status, x.maturity FROM firm.assessment x ${ACTIVE} WHERE x.cycle_id = N'${LEADERSHIP_CYCLE}'`);
    const reviewed = await query(`
    SELECT x.firm_code AS firmCode, x.control_id AS controlId, x.reviewed_status AS status, x.reviewed_maturity AS maturity
    FROM firm.control_submission x ${ACTIVE}
    WHERE x.status = N'accepted'
      AND x.reviewed_at = (SELECT MAX(y.reviewed_at) FROM firm.control_submission y WHERE y.firm_code = x.firm_code AND y.control_id = x.control_id AND y.status = N'accepted')`);
    const self = await query(`SELECT x.firm_code AS firmCode, x.control_id AS controlId, x.status, x.maturity FROM firm.self_assessment x ${ACTIVE}`);
    const plans = await query(`SELECT x.firm_code AS firmCode, x.delivery_status AS status FROM firm.action_plan x ${ACTIVE}`);
    const risks = await query(`SELECT r.firm_code AS firmCode, COUNT(*) AS n FROM firm.risk r WHERE ${HIGHER_RISKS_OPEN_WHERE} GROUP BY r.firm_code`);
    const stages = await query(`SELECT x.firm_code AS firmCode, x.stage FROM firm.program_status x ${ACTIVE}`);
    const mapFor = (rows, code) => new Map(rows.filter((x) => x.firmCode === code).map((x) => [x.controlId, { status: x.status ?? null, maturity: x.maturity ?? null }]));
    return firms.filter((f) => !isFormerFirm(f.firmCode)).map((f) => {
        const code = f.firmCode;
        const c = mapFor(cdw, code);
        const r = mapFor(reviewed, code);
        const s = mapFor(self, code);
        const rows = effectiveRows(controls, c, r, s);
        return {
            firmCode: code,
            firmName: f.firmName,
            isSynthetic: Boolean(f.isSynthetic),
            source: firmSource(c, r, s),
            rows,
            measures: firmMeasures(rows),
            plans: planCounts(plans.filter((p) => p.firmCode === code).map((p) => p.status)),
            stage: stages.find((x) => x.firmCode === code)?.stage ?? null,
            higherRisksOpen: c.size ? Number(risks.find((x) => x.firmCode === code)?.n ?? 0) : null,
        };
    });
}
//# sourceMappingURL=leadership-measures.js.map